'use client';

import { Accordion, AccordionItem, Button, Card, CardBody } from '@nextui-org/react';
import { ChevronDown, LogIn, UserCircle, UserPlus } from 'lucide-react';

import { Link } from '@/shared/navigation';
import { ROUTES } from '@/shared/routes/routes';

const SupportLink = () => (
  <Link
    href={ROUTES.contacts}
    className="font-medium text-warning-700 dark:text-warning-400 underline underline-offset-2 hover:no-underline"
  >
    напишите в поддержку
  </Link>
);

export const HomeFaq = () => {
  return (
    <section
      className="w-full py-10 md:py-14"
      aria-labelledby="home-faq-heading"
    >
      <h2
        id="home-faq-heading"
        className="text-center text-2xl font-bold text-foreground md:text-3xl mb-3"
      >
        Частые вопросы
      </h2>
      <p className="mx-auto mb-8 max-w-2xl text-center text-sm text-default-600 md:text-base">
        Не нашли ответ —{' '}
        <Link
          href={ROUTES.contacts}
          className="font-medium text-warning-700 dark:text-warning-400 underline underline-offset-2 hover:no-underline"
        >
          свяжитесь с нами
        </Link>
        .
      </p>

      <Card
        className="mx-auto mb-10 max-w-3xl border border-warning/25 bg-warning/5 shadow-sm"
        role="region"
        aria-labelledby="home-faq-account-heading"
      >
        <CardBody className="gap-4 p-5 md:flex-row md:items-center md:gap-6 md:p-6 flex flex-col">
          <div className="flex shrink-0 items-center justify-center rounded-2xl bg-warning/15 p-3 text-warning">
            <UserCircle className="h-10 w-10 md:h-12 md:w-12" aria-hidden />
          </div>
          <div className="min-w-0 flex-1 text-center md:text-left">
            <h3
              id="home-faq-account-heading"
              className="text-lg font-semibold text-foreground md:text-xl"
            >
              Личный кабинет и скачивание
            </h3>
            <p className="mt-2 text-sm text-default-600 md:text-base">
              Зарегистрируйтесь или войдите в аккаунт — купленные мастер-классы
              можно скачать в любой момент в разделе «Мои мастер-классы».
              Материалы по-прежнему дублируются на почту после оплаты.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 md:justify-start">
              <Button
                as={Link}
                href={ROUTES.login}
                color="warning"
                variant="flat"
                radius="full"
                size="sm"
                className="font-semibold text-warning-800 dark:text-warning-300"
                startContent={<LogIn className="h-4 w-4" aria-hidden />}
              >
                Войти
              </Button>
              <Button
                as={Link}
                href={ROUTES.register}
                variant="bordered"
                color="warning"
                radius="full"
                size="sm"
                className="font-semibold text-warning-800 dark:text-warning-300 border-warning-500"
                startContent={<UserPlus className="h-4 w-4" aria-hidden />}
              >
                Регистрация
              </Button>
              <Link
                href={ROUTES.meMasterclasses}
                className="text-sm font-semibold text-warning-700 dark:text-warning-400 underline underline-offset-2 hover:no-underline"
              >
                Мои мастер-классы →
              </Link>
            </div>
          </div>
        </CardBody>
      </Card>

      <Accordion
        variant="splitted"
        className="max-w-3xl mx-auto px-0 gap-2"
        itemClasses={{
          title: 'font-medium text-foreground text-left text-sm md:text-base',
          trigger: 'py-4',
          content: 'text-default-600 text-sm md:text-base pb-4',
        }}
      >
        <AccordionItem
          key="1"
          aria-label="Что приходит после оплаты"
          title="Что я получу после покупки мастер-класса?"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <p>
            После успешной оплаты материалы мастер-класса отправляются на email,
            указанный при оформлении заказа. Сохраните письмо — файлы остаются у
            вас.
          </p>
        </AccordionItem>
        <AccordionItem
          key="yandex-email"
          aria-label="Почта Яндекса"
          title="Можно ли указать почту Яндекса?"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <p>
            Сейчас нет — письма с мастер-классами на Яндекс.Почту (yandex.ru,
            ya.ru) не доходят. Укажите другой адрес: Gmail, Mail.ru или любой
            другой, кроме Яндекса.
          </p>
        </AccordionItem>
        <AccordionItem
          key="2"
          aria-label="Письмо не пришло"
          title="Мне не пришло письмо с мастер-классом"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <div className="flex flex-col gap-2">
            <p>
              Проверьте папки «Спам», «Промо» и другие — письма с вложениями
              иногда попадают туда.
            </p>
            <p>
              Убедитесь, что открываете тот же почтовый ящик, который указывали при
              оплате. Подождите несколько минут — иногда доставка задерживается.
            </p>
            <p>
              Если письма нет — <SupportLink />, укажите почту заказа и мы
              поможем.
            </p>
          </div>
        </AccordionItem>
        <AccordionItem
          key="3"
          aria-label="Ошибка в почте"
          title="Ошибся в адресе почты при покупке"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <p>
            Напишите в поддержку с чеком или скрином оплаты и актуальным адресом
            почты — <SupportLink />.
          </p>
        </AccordionItem>
        <AccordionItem
          key="4"
          aria-label="Формат материалов"
          title="В каком формате мастер-классы?"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <p>
            Обычно это PDF и дополнительные материалы, описанные на странице
            конкретного мастер-класса. Точный состав смотрите в карточке товара.
          </p>
        </AccordionItem>
        <AccordionItem
          key="5"
          aria-label="Аккаунт и скачивание"
          title="Зачем входить в аккаунт и где скачать мастер-классы?"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <div className="flex flex-col gap-3">
            <p>
              После{' '}
              <Link
                href={ROUTES.register}
                className="font-medium text-warning-700 dark:text-warning-400 underline underline-offset-2 hover:no-underline"
              >
                регистрации
              </Link>{' '}
              или{' '}
              <Link
                href={ROUTES.login}
                className="font-medium text-warning-700 dark:text-warning-400 underline underline-offset-2 hover:no-underline"
              >
                входа
              </Link>{' '}
              ваши покупки привязаны к профилю: в разделе{' '}
              <Link
                href={ROUTES.meMasterclasses}
                className="font-medium text-warning-700 dark:text-warning-400 underline underline-offset-2 hover:no-underline"
              >
                «Мои мастер-классы»
              </Link>{' '}
              можно скачать файлы повторно в любое время.
            </p>
            <p>
              Письмо с материалами на email приходит и без аккаунта — кабинет
              удобен, чтобы не искать вложения в почте и быстро получить PDF.
            </p>
          </div>
        </AccordionItem>
        <AccordionItem
          key="6"
          aria-label="Как заказать мастер-класс"
          title="Как заказать мастер-класс по амигуруми?"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <p>
            Выберите игрушку в каталоге мастер-классов, добавьте в корзину и
            оплатите — PDF со схемой придёт на почту сразу после оплаты.
            Индивидуальную разработку схемы под конкретную игрушку я не
            делаю.
          </p>
        </AccordionItem>
        <AccordionItem
          key="7"
          aria-label="Курс или мастер-класс"
          title="В чём разница между курсом и мастер-классом?"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <p>
            Мастер-класс — это подробный PDF на одну конкретную игрушку.{' '}
            <Link
              href={ROUTES.course}
              className="font-medium text-warning-700 dark:text-warning-400 underline underline-offset-2 hover:no-underline"
            >
              Курс по вязанию игрушек крючком
            </Link>
            , который я сейчас готовлю, объединит несколько мастер-классов в
            последовательную программу от простого к сложному. Пока курс не
            вышел, начать вязать можно уже по отдельным мастер-классам из
            каталога.
          </p>
        </AccordionItem>
        <AccordionItem
          key="8"
          aria-label="Продажа готовых игрушек"
          title="Вы продаёте готовые вязаные игрушки?"
          indicator={<ChevronDown className="h-4 w-4 shrink-0" aria-hidden />}
        >
          <p>
            Нет, на сайте продаются только мастер-классы (PDF-схемы с
            описанием), а не готовые игрушки. Стоимость каждого мастер-класса
            указана на его странице в каталоге.
          </p>
        </AccordionItem>
      </Accordion>
    </section>
  );
};
